import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { fundingNavigationManifest } from '../generated/navigation.js';
import { bindOnrampCache } from '../operations/cache-lifecycle.js';
import { isWithdrawalMethod } from './types.js';
export function useWithdrawalCheckout(client, surface, createId, active = true, initialCurrency = 'USD', locale = 'en', realtime) {
  const cache = useQueryClient();
  bindOnrampCache(client, cache);
  const [currency, setCurrency] = useState(initialCurrency);
  const [screen, setScreen] = useState('payouts');
  const [method, setMethod] = useState(null);
  const [bankPreference, setBankPreference] = useState(null);
  const [recipient, setRecipient] = useState('');
  const [amount, setAmount] = useState('');
  const [preview, setPreview] = useState(null);
  const [operationId, setOperationId] = useState(null);
  const [hostedUrl, setHostedUrl] = useState(null);
  const key = useRef(null);
  const signed = useRef(null);
  const context = useQuery({
    queryKey: ['onramp', client.cacheKey, 'withdrawal-context', currency, surface],
    queryFn: ({
      signal
    }) => client.withdrawalContext(currency, surface, signal),
    staleTime: 30000,
    enabled: active,
    meta: {
      persist: false
    }
  });
  const activeId = operationId ?? context.data?.pendingId ?? null;
  const operation = useQuery({
    queryKey: ['onramp', client.cacheKey, 'operation', activeId],
    queryFn: ({
      signal: signal_0
    }) => {
      if (!activeId) throw new Error('offramp_operation_missing');
      return client.withdrawal(activeId, signal_0);
    },
    enabled: active && activeId !== null,
    meta: {
      persist: false
    },
    refetchInterval: query => !active || query.state.data && ['delivered', 'failed', 'cancelled', 'refunded'].includes(query.state.data.status) ? false : 7000
  });
  const resumed = useQuery({
    queryKey: ['onramp', client.cacheKey, 'withdrawal-checkout', activeId, operation.data?.withdrawal?.providerId],
    queryFn: ({
      signal: signal_1
    }) => {
      if (!activeId) throw new Error('offramp_operation_missing');
      return client.resumeWithdrawal(activeId, signal_1);
    },
    enabled: active && activeId !== null && operation.data?.withdrawal?.kind === 'sell',
    staleTime: 30000,
    meta: {
      persist: false
    }
  });
  useEffect(() => {
    if (!active || !activeId || !realtime) return;
    return realtime.subscribe(id => {
      if (id === activeId) void cache.invalidateQueries({
        queryKey: ['onramp', client.cacheKey, 'operation', id],
        exact: true
      });
    });
  }, [active, activeId, realtime, cache, client]);
  const commands = useMutation({
    mutationFn: async command => {
      if (command.kind === 'quote') {
        key.current = null;
        setAmount(command.amountD6);
        if (method === 'manual_withdraw') setScreen('review');else if (method) {
          setPreview(await client.quoteWithdrawal({
            method,
            amountD6: command.amountD6,
            currency,
            surface
          }));
          setScreen('review');
        }
      } else if (command.kind === 'create') {
        if (!method) throw new Error('offramp_method_missing');
        const idempotencyKey = key.current ?? createId();
        key.current = idempotencyKey;
        const result = await client.createWithdrawal({
          idempotencyKey,
          method,
          amountD6: amount,
          currency,
          surface,
          locale,
          ...(bankPreference ? {
            bankPreference: bankPreference.id
          } : {}),
          ...(method === 'manual_withdraw' ? {
            recipient
          } : preview ? {
            payoutUnits: preview.payoutUnits,
            feeUnits: preview.feeUnits
          } : {})
        });
        setOperationId(result.operation.id);
        setHostedUrl(result.url);
        setScreen(result.url ? 'payout_account' : 'status');
      } else if (command.kind === 'send') {
        if (!activeId) throw new Error('offramp_operation_missing');
        const reviewed = operation.data;
        if (!reviewed) throw new Error('offramp_review_missing');
        const reserved = await client.reserveWithdrawal(activeId, reviewed.version, true);
        if (!reserved.executable) {
          await operation.refetch();
          setScreen('status');
          return;
        }
        setScreen('status');
        let authorizationAttempted = false;
        const signature = await command.send(reserved.operation.destination, reserved.recipient, reserved.amountD6, reserved.operation.network, activeId, reserved.mint, async () => {
          if (authorizationAttempted) throw new Error('offramp_submission_unknown');
          authorizationAttempted = true;
          const authorization = await client.reserveWithdrawal(activeId, reserved.operation.version);
          if (!authorization.executable) throw new Error('offramp_review_changed');
        });
        signed.current = {
          id: activeId,
          signature
        };
        await client.reportWithdrawal(activeId, signature);
        signed.current = null;
        await operation.refetch();
      } else if (command.kind === 'report' && signed.current) {
        await client.reportWithdrawal(signed.current.id, signed.current.signature);
        signed.current = null;
        await operation.refetch();
      } else if (activeId) {
        await client.cancelWithdrawal(activeId);
        await operation.refetch();
      }
    }
  });
  function select(next, bank) {
    if (commands.isPending || activeId) return;
    setMethod(next);
    setBankPreference(bank ?? null);
    setPreview(null);
    key.current = null;
    setScreen(fundingNavigationManifest.offramp.events[next === 'manual_withdraw' ? 'manual_selected' : 'method_selected']);
  }
  function back() {
    if (commands.isPending || activeId) return;
    const destination = fundingNavigationManifest.offramp.screens[screen].back;
    if (destination) setScreen(method === 'manual_withdraw' && screen === 'amount' ? 'recipient' : destination);
  }
  return {
    context,
    operation,
    activeId,
    hostedUrl: resumed.data ? resumed.data.url : hostedUrl,
    screen: activeId && screen !== 'payout_account' ? 'status' : screen,
    method: method ?? (operation.data && isWithdrawalMethod(operation.data.method) ? operation.data.method : null),
    bankPreference,
    recipient,
    setRecipient,
    amount,
    preview,
    currency,
    setCurrency: next_0 => {
      if (commands.isPending || activeId) return;
      setCurrency(next_0);
      setPreview(null);
      key.current = null;
    },
    finish: () => {
      setOperationId(null);
      setHostedUrl(null);
      setScreen('payouts');
      setMethod(null);
      setAmount('');
      setRecipient('');
      setPreview(null);
      setBankPreference(null);
      key.current = null;
      signed.current = null;
      commands.reset();
      void context.refetch();
    },
    canReport: signed.current !== null,
    resumeError: resumed.error,
    retryOperation: () => {
      void operation.refetch();
      if (operation.data?.withdrawal?.kind === 'sell') void resumed.refetch();
    },
    select,
    back,
    recipientReady: () => setScreen('amount'),
    commands
  };
}